import React from 'react';
import { jsPDF } from 'jspdf';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { loadLogoPng, addBrandHeader, addFooters, SLATE_900, SLATE_600, BLUE, BORDER } from '../utils/tripExport';

// Gather every stylesheet rule accessible on the page so the exported HTML guide keeps the app styling.
const collectPageCSS = () => {
  let css = '';
  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules) css += rule.cssText + '\n';
    } catch { /* cross-origin sheet — skip */ }
  }
  return css;
};

// Same branded shell as the History/Cost report HTML exports (header bar + footer + print rules).
const buildHelpDocHTML = (title, subtitle, pageCss, bodyHTML, ts, logoSrc) => `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${title}</title>
<style>${pageCss}</style>
<style>body{margin:0;font-family:system-ui,-apple-system,sans-serif;background:#fff;color:#1e293b}
.hx-header{height:60px;background:#fff;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;padding:0 16px;gap:16px}
.hx-header img{height:36px}.hx-header-info{font-size:13px;color:#475569}.hx-header-info b{color:#1e293b}
.hx-content{padding:20px;max-width:760px}
.hx-footer{margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#475569;text-align:center}
@media print{.hx-header{position:fixed;top:0;left:0;right:0;border-bottom:2px solid #2563eb}.hx-content{margin-top:70px}@page{margin:10mm 8mm}}</style></head><body>
<div class="hx-header">${logoSrc ? `<img src="${logoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="hx-header-info"><b>${title}</b>${subtitle ? ' &mdash; ' + subtitle : ''}</div></div>
<div class="hx-content">${bodyHTML}<div class="hx-footer">Generated by EnsueLIVETRAK &mdash; ${ts}</div></div></body></html>`;

/* jsPDF standard fonts are WinAnsi-only (same constraint as tripExport). */
const PDF_CHAR_MAP = {
  '\u2192': '->', '\u2190': '<-', '\u2018': "'", '\u2019': "'", '\u201c': '"', '\u201d': '"',
  '\u2022': '-', '\u2265': '>=', '\u2264': '<=', '\u2713': '*', '\u2714': '*', '\u00b7': '-',
};
const sanitizePdf = (s) => String(s ?? '')
  .replace(/[\u2192\u2190\u2018\u2019\u201c\u201d\u2022\u2265\u2264\u2713\u2714\u00b7]/g, (c) => PDF_CHAR_MAP[c])
  .replace(/[^\x20-\x7E\u00A0-\u00FF\u2013\u2014\u2026]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

// Walk the help panel DOM into simple blocks: headings, paragraphs, bullets, tables.
const INLINE_TAGS = ['SPAN', 'B', 'STRONG', 'A', 'EM', 'CODE', 'KBD', 'SVG', 'I', 'BR'];
const extractBlocks = (root) => {
  const blocks = [];
  const walk = (el) => {
    if (el.nodeType !== 1) return;
    const tag = el.tagName;
    if (tag === 'TABLE') {
      const headers = [...el.querySelectorAll('thead th')].map((th) => th.innerText.trim());
      const rows = [...el.querySelectorAll('tbody tr')].map((tr) => [...tr.children].map((td) => td.innerText.trim()));
      if (rows.length || headers.length) blocks.push({ type: 'table', headers, rows });
      return;
    }
    if (/^H[1-6]$/.test(tag)) {
      const t = el.innerText.trim();
      if (t) blocks.push({ type: 'heading', text: t });
      return;
    }
    if (tag === 'P' || tag === 'LI') {
      const t = el.innerText.trim();
      if (t) blocks.push({ type: tag === 'LI' ? 'bullet' : 'para', text: t });
      return;
    }
    const kids = [...el.children];
    if (!kids.length || kids.every((k) => INLINE_TAGS.includes(k.tagName))) {
      const t = el.innerText ? el.innerText.trim() : '';
      if (t) blocks.push({ type: 'para', text: t });
      return;
    }
    kids.forEach(walk);
  };
  walk(root);
  return blocks;
};

// Direct branded PDF download — same pipeline as the Driver Log Book / History exports.
const buildHelpPdf = (blocks, { slug, title, subtitle }, logo) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const M = 40;
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const layout = { M, PAGE_W, PAGE_H, RIGHT: PAGE_W - M, CONTENT_W: PAGE_W - 2 * M };
  const BOTTOM = PAGE_H - 44;
  let y = addBrandHeader(doc, layout, logo, title, subtitle || '');

  const ensure = (h) => { if (y + h > BOTTOM) { doc.addPage(); y = 44; } };

  const drawTableRow = (cells, colW, bold, bg) => {
    doc.setFontSize(7.5);
    doc.setFont(undefined, bold ? 'bold' : 'normal');
    const wrapped = cells.map((c) => doc.splitTextToSize(sanitizePdf(c), colW - 10));
    const h = Math.max(1, ...wrapped.map((l) => l.length)) * 9 + 8;
    ensure(h);
    if (bg) { doc.setFillColor(...bg); doc.rect(M, y, layout.CONTENT_W, h, 'F'); }
    doc.setTextColor(...(bold ? SLATE_600 : SLATE_900));
    wrapped.forEach((lines, i) => doc.text(lines, M + i * colW + 5, y + 11));
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.rect(M, y, layout.CONTENT_W, h);
    y += h;
  };

  blocks.forEach((b) => {
    if (b.type === 'heading') {
      ensure(30);
      y += 10;
      doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(...BLUE);
      doc.text(sanitizePdf(b.text), M, y);
      y += 6;
      doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
      doc.line(M, y, layout.RIGHT, y);
      y += 12;
    } else if (b.type === 'table') {
      const cols = Math.max(b.headers.length, ...(b.rows.length ? b.rows.map((r) => r.length) : [1]), 1);
      const colW = layout.CONTENT_W / cols;
      if (b.headers.length) drawTableRow(b.headers, colW, true, [241, 245, 249]);
      b.rows.forEach((r, i) => drawTableRow(r, colW, false, i % 2 === 1 ? [248, 250, 252] : null));
      y += 8;
    } else {
      const indent = b.type === 'bullet' ? 10 : 0;
      const prefix = b.type === 'bullet' ? '-  ' : '';
      doc.setFontSize(8.5); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_900);
      const lines = doc.splitTextToSize(prefix + sanitizePdf(b.text), layout.CONTENT_W - indent);
      ensure(lines.length * 11.5 + 4);
      doc.text(lines, M + indent, y);
      y += lines.length * 11.5 + 5;
    }
  });

  addFooters(doc, layout);
  doc.save(`${slug}_guide.pdf`);
};

export const HelpDocExport = ({ slug, title, subtitle, targetId }) => {
  const doExport = async (fmt) => {
    const node = document.getElementById(targetId);
    if (!node) { toast.error('Nothing to export'); return; }
    let logo = null;
    try { logo = await loadLogoPng(); } catch { /* export continues without logo */ }
    if (fmt === 'pdf') {
      try {
        buildHelpPdf(extractBlocks(node), { slug, title, subtitle }, logo);
        toast.success('Guide exported as PDF');
      } catch (e) {
        console.error('Help PDF export failed:', e);
        toast.error('PDF export failed');
      }
    } else {
      const html = buildHelpDocHTML(title, subtitle, collectPageCSS(), node.innerHTML, new Date().toLocaleString(), logo?.dataUrl || '');
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}_guide.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Guide exported as HTML');
    }
  };
  return (
    <div className="flex justify-end pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-7 text-[10px] gap-1" data-testid={`help-export-${slug}-menu`}>
            <Upload className="w-3 h-3" /> Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[10001]">
          <DropdownMenuItem onClick={() => doExport('html')} data-testid={`help-export-${slug}-html`}>HTML</DropdownMenuItem>
          <DropdownMenuItem onClick={() => doExport('pdf')} data-testid={`help-export-${slug}-pdf`}>PDF</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
